import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, InputLabel, MenuItem,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import { get } from "utils/WebService";
import { avisarFichaAlterada } from "utils/fichaAlterada";
import { ehCarteiraDeCobranca, ehCarteiraJuridica } from "utils/Carteiras";
import { avisarSolicitacoesAlteradas, enviarComAnexos, listaDe } from "./solicitacaoUtils";

const rotulo = { px: 1, pb: 0.5, pt: 0.2 };

const SITUACAO_DEBITO = { 1: "Aberto", 2: "Baixado parcial", 3: "Liquidado", 4: "Devolvido", 6: "Migrado" };
const moeda = (v) => (v === null || v === undefined ? "-" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
const dataBr = (iso) => (iso ? iso.split("-").reverse().join("/") : "-");

/** Os débitos que cada ação aceita: devolução só os não devolvidos; reativação só os devolvidos; o resto, todos. */
const debitosDaAcao = (acao, debitos) => {
    if (acao === "DEVOLUCAO_CONTRATO") return debitos.filter(d => d.situacao !== 4);
    if (acao === "REATIVACAO_CONTRATO") return debitos.filter(d => d.situacao === 4);
    return debitos;
};

/**
 * Nova solicitação ao suporte (tela Solicitações ao Suporte e botão da ficha): o tipo da lista do suporte, a ficha
 * (já vem a da tela, quando aberta pela ficha), os débitos (quando o tipo pede), a descrição e os anexos. Na migração de
 * carteira, também a carteira de destino e, fora da jurídica, o responsável e o status. Para a carteira jurídica quem pede
 * só pede (pedido da Aline em 25/10/2026: a Extra não tem esses dados): o suporte aprova e o CobJur escolhe o negociador e
 * preenche os Dados do Jurídico em Migrações para o Jurídico.
 */
export default function NovaSolicitacaoDialog({ open, onClose, idFichaInicial, onCriada }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const [tipos, setTipos] = useState([]);
    const [idTipo, setIdTipo] = useState("");
    const [busca, setBusca] = useState("");
    const [buscando, setBuscando] = useState(false);
    const [resultados, setResultados] = useState(null);
    const [ficha, setFicha] = useState(null);
    const [debitos, setDebitos] = useState([]);
    const [marcados, setMarcados] = useState([]);
    const [descricao, setDescricao] = useState("");
    const [arquivos, setArquivos] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState("");
    // Migração de carteira
    const [carteirasDestino, setCarteirasDestino] = useState([]);
    const [statusFicha, setStatusFicha] = useState([]);
    const [usuariosDestino, setUsuariosDestino] = useState([]);
    const [carteiraId, setCarteiraId] = useState("");
    const [usuarioId, setUsuarioId] = useState("");
    const [statusId, setStatusId] = useState("");

    useEffect(() => {
        if (!open) return;
        setIdTipo(""); setBusca(""); setResultados(null); setFicha(null); setDebitos([]); setMarcados([]);
        setDescricao(""); setArquivos([]); setErro("");
        setCarteiraId(""); setUsuarioId(""); setStatusId("");
        get("/solicitacoes-suporte/tipos").then(res => setTipos(res?.tipos || [])).catch(e => setErro(e.message || "Erro ao carregar os tipos."));
        if (idFichaInicial) {
            get(`/solicitacoes-suporte/fichas/${idFichaInicial}`)
                .then(res => { setFicha(res); setDebitos(res?.debitos || []); })
                .catch(e => setErro(e.message || "Erro ao carregar a ficha."));
        }
    }, [open, idFichaInicial]);

    const tipo = tipos.find(t => String(t.id) === String(idTipo));
    const acao = tipo?.acao;
    const migracao = acao === "MIGRACAO_CARTEIRA";
    const mostraDebitos = !!ficha && !!tipo && !["DEVOLUCAO_FICHA", "REATIVACAO_FICHA"].includes(acao);
    // Migração: carteiras de cobrança, menos a da ficha; jurídica = mostra os Dados do Jurídico (pelo tipo da carteira)
    const destinos = useMemo(() => carteirasDestino.filter(c => String(c.id || c.cvId) !== String(ficha?.idCarteira)), [carteirasDestino, ficha]);
    const destino = destinos.find(c => String(c.id || c.cvId) === String(carteiraId));
    const destinoJuridico = migracao && ehCarteiraJuridica(destino);
    const debitosDoTipo = useMemo(() => debitosDaAcao(acao, debitos), [acao, debitos]);
    const pedeDebitos = !!tipo && (tipo.exigeDebitos || acao === "DEVOLUCAO_CONTRATO" || acao === "REATIVACAO_CONTRATO");

    // Migração: carteiras e status (uma vez), o código do cliente do devedor e as pessoas da carteira de destino
    useEffect(() => {
        if (!open || !migracao || carteirasDestino.length > 0) return;
        get("/carteira/destinos-migracao").then(res => setCarteirasDestino(listaDe(res).filter(ehCarteiraDeCobranca))).catch(() => setCarteirasDestino([]));
        get("/status-ficha/list-all").then(res => setStatusFicha(listaDe(res))).catch(() => setStatusFicha([]));
    }, [open, migracao]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setUsuarioId("");
        if (!carteiraId) { setUsuariosDestino([]); return; }
        // As pessoas das equipes da carteira de destino (quem pede não está nelas)
        get(`/users/list-all-operators?carteiraId=${carteiraId}&destinoMigracao=true`)
            .then(res => setUsuariosDestino(listaDe(res)))
            .catch(() => setUsuariosDestino([]));
    }, [carteiraId]);

    // Trocar o tipo tira os débitos que ele não aceita
    useEffect(() => {
        setMarcados(prev => prev.filter(id => debitosDoTipo.some(d => d.id === id)));
    }, [debitosDoTipo]);

    // Só a resposta da última busca vale (a busca roda enquanto a pessoa digita)
    const ultimaBusca = useRef(0);
    const buscar = (texto = busca) => {
        const termo = texto.trim();
        if (termo.length < 2) return;
        const numero = ++ultimaBusca.current;
        setBuscando(true);
        setErro("");
        get(`/solicitacoes-suporte/fichas?q=${encodeURIComponent(termo)}`)
            .then(res => { if (numero === ultimaBusca.current) setResultados(listaDe(res)); })
            .catch(e => { if (numero === ultimaBusca.current) setErro(e.message || "Erro na busca."); })
            .finally(() => { if (numero === ultimaBusca.current) setBuscando(false); });
    };

    // Busca sozinha 0,4 s depois de parar de digitar (3 letras ou mais); o botão Buscar e o Enter continuam
    useEffect(() => {
        if (ficha || busca.trim().length < 3) return undefined;
        const timer = setTimeout(() => buscar(busca), 400);
        return () => clearTimeout(timer);
    }, [busca, ficha]); // eslint-disable-line react-hooks/exhaustive-deps

    const escolherFicha = (f) => {
        setFicha(f);
        setMarcados([]);
        get(`/solicitacoes-suporte/fichas/${f.idFicha}/debitos`).then(res => setDebitos(listaDe(res))).catch(() => setDebitos([]));
    };

    const alternar = (id) => setMarcados(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

    const faltando = !tipo ? "Escolha o tipo da solicitação."
        : !ficha ? "Escolha a ficha."
            : migracao && !carteiraId ? "Escolha a carteira de destino."
                : pedeDebitos && marcados.length === 0 ? "Marque os débitos."
                    : descricao.trim().length < 5 ? "Descreva o pedido."
                        : tipo.exigeAnexo && arquivos.length === 0 ? "Anexe o documento pedido."
                            : "";

    const enviar = async () => {
        if (faltando) { setErro(faltando); return; }
        setEnviando(true);
        setErro("");
        try {
            const res = await enviarComAnexos("/solicitacoes-suporte/abrir", {
                dados: JSON.stringify({
                    idTipo: tipo.id,
                    idFicha: ficha.idFicha,
                    contratosIds: marcados,
                    descricao: descricao.trim(),
                    // Migração: o suporte confere e pode trocar na aprovação
                    migracao: migracao ? {
                        carteiraId,
                        // Para a carteira jurídica, o negociador, o status e os Dados do Jurídico ficam para o CobJur
                        usuarioId: destinoJuridico ? null : (usuarioId || null),
                        statusFichaId: destinoJuridico ? null : (statusId || null),
                        pacote: null,
                    } : null,
                }),
            }, arquivos);
            avisarSolicitacoesAlteradas();
            avisarFichaAlterada();
            if (onCriada) onCriada(res);
            onClose();
        } catch (e) {
            setErro(e.message || "Erro ao enviar a solicitação.");
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>Nova solicitação ao suporte</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <InputLabel sx={rotulo}>Tipo *</InputLabel>
                        <MDInput select fullWidth size="small" value={idTipo} onChange={e => setIdTipo(e.target.value)} SelectProps={{ displayEmpty: true }}>
                            <MenuItem value="" disabled>Escolha o tipo</MenuItem>
                            {tipos.map(t => <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>)}
                        </MDInput>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {tipo?.orientacao && (
                            <MDBox mt={{ xs: 0, md: 2.5 }} p={1} sx={{ borderRadius: "6px", backgroundColor: cores.painel, border: `1px solid ${cores.borda}` }}>
                                <MDTypography variant="caption" color="text" display="block">
                                    <Icon sx={{ fontSize: "14px !important", verticalAlign: "middle", mr: 0.5 }}>info</Icon>
                                    {tipo.orientacao}
                                </MDTypography>
                            </MDBox>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <InputLabel sx={rotulo}>Ficha *</InputLabel>
                        {ficha ? (
                            <MDBox display="flex" alignItems="center" justifyContent="space-between" p={1}
                                sx={{ borderRadius: "6px", border: `1px solid ${cores.borda}`, backgroundColor: cores.painel }}>
                                <MDBox>
                                    <MDTypography variant="button" fontWeight="bold" display="block">
                                        Ficha #{ficha.numero} · {ficha.nomeDevedor}
                                    </MDTypography>
                                    <MDTypography variant="caption" color="text">
                                        {[ficha.cliente, ficha.carteira, ficha.situacaoLabel, ficha.codigoCliente && `Cód. ${ficha.codigoCliente}`].filter(Boolean).join(" · ")}
                                    </MDTypography>
                                </MDBox>
                                {!idFichaInicial && (
                                    <MDButton variant="text" color="info" size="small" onClick={() => { setFicha(null); setDebitos([]); setMarcados([]); }}>
                                        Trocar
                                    </MDButton>
                                )}
                            </MDBox>
                        ) : (
                            <>
                                <MDBox display="flex" gap={1}>
                                    <MDInput fullWidth size="small" value={busca} onChange={e => setBusca(e.target.value)}
                                        placeholder="Nº da ficha, código do cliente, CPF/CNPJ ou nome do devedor"
                                        onKeyDown={e => { if (e.key === "Enter") buscar(); }} />
                                    <MDButton variant="contained" color="info" size="medium" onClick={() => buscar()} disabled={buscando} sx={{ height: 38 }}>
                                        {buscando ? <CircularProgress size={16} color="inherit" /> : "Buscar"}
                                    </MDButton>
                                </MDBox>
                                {resultados && (
                                    <MDBox mt={1} sx={{ maxHeight: 220, overflowY: "auto", border: `1px solid ${cores.borda}`, borderRadius: "6px" }}>
                                        {resultados.length === 0 && (
                                            <MDTypography variant="caption" color="text" display="block" p={1}>
                                                Nenhuma ficha encontrada entre as que você vê.
                                            </MDTypography>
                                        )}
                                        {resultados.map(f => (
                                            <MDBox key={f.idFicha} px={1} py={0.75} onClick={() => escolherFicha(f)}
                                                sx={{ cursor: "pointer", borderBottom: `1px solid ${cores.divisoria}`, "&:hover": { backgroundColor: cores.hover } }}>
                                                <MDTypography variant="caption" fontWeight="bold" display="block">
                                                    Ficha #{f.numero} · {f.nomeDevedor}{f.honorario ? " (honorários)" : ""}
                                                </MDTypography>
                                                <MDTypography variant="caption" color="text">
                                                    {[f.cliente, f.carteira, f.situacaoLabel, f.codigoCliente && `Cód. ${f.codigoCliente}`].filter(Boolean).join(" · ")}
                                                </MDTypography>
                                            </MDBox>
                                        ))}
                                    </MDBox>
                                )}
                            </>
                        )}
                    </Grid>

                    {migracao && ficha && (
                        <>
                            <Grid item xs={12} md={4}>
                                <InputLabel sx={rotulo}>Carteira de destino *</InputLabel>
                                <MDInput select fullWidth size="small" value={carteiraId} onChange={e => setCarteiraId(e.target.value)} SelectProps={{ displayEmpty: true }}>
                                    <MenuItem value="" disabled>Escolha a carteira</MenuItem>
                                    {destinos.map(c => <MenuItem key={c.id || c.cvId} value={c.id || c.cvId}>{c.nome || c.ccNome}</MenuItem>)}
                                </MDInput>
                            </Grid>
                            {!destinoJuridico && (
                            <>
                            <Grid item xs={12} md={4}>
                                <InputLabel sx={rotulo}>Responsável / negociador</InputLabel>
                                <MDInput select fullWidth size="small" value={usuarioId} onChange={e => setUsuarioId(e.target.value)} SelectProps={{ displayEmpty: true }}
                                    disabled={!carteiraId}>
                                    <MenuItem value="">Mantém o atual</MenuItem>
                                    {usuariosDestino.map(u => <MenuItem key={u.id || u.cvId} value={u.id || u.cvId}>{u.nome || u.ccNome}</MenuItem>)}
                                </MDInput>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputLabel sx={rotulo}>Status da ficha</InputLabel>
                                <MDInput select fullWidth size="small" value={statusId} onChange={e => setStatusId(e.target.value)} SelectProps={{ displayEmpty: true }}>
                                    <MenuItem value="">Mantém o atual</MenuItem>
                                    {statusFicha.map(st => <MenuItem key={st.id || st.cvId} value={st.id || st.cvId}>{st.nome || st.ccNome}</MenuItem>)}
                                </MDInput>
                            </Grid>
                            </>
                            )}
                            <Grid item xs={12} sx={{ pt: "4px !important" }}>
                                <MDTypography variant="caption" color="text" display="block" px={1}>
                                    {destinoJuridico
                                        ? "Para a carteira jurídica você só pede: o pedido vai primeiro ao Jurídico (CobJur), que escolhe o negociador, preenche os "
                                            + "dados (código novo, débito, processo) e envia ao suporte, que conclui a migração. Até lá, a ficha não recebe acordo."
                                        : "O suporte confere a carteira, o responsável e o status e pode ajustar ao aprovar."}
                                </MDTypography>
                            </Grid>
                        </>
                    )}

                    {mostraDebitos && (
                        <Grid item xs={12}>
                            <InputLabel sx={rotulo}>
                                Débitos {pedeDebitos ? "*" : "(opcional)"}
                                {acao === "REATIVACAO_CONTRATO" ? " · só os devolvidos" : acao === "DEVOLUCAO_CONTRATO" ? " · só os que não estão devolvidos"
                                    : migracao ? " · sem marcar nenhum, migra a ficha inteira" : ""}
                            </InputLabel>
                            <MDBox sx={{ maxHeight: 200, overflowY: "auto", border: `1px solid ${cores.borda}`, borderRadius: "6px" }}>
                                {debitosDoTipo.length === 0 && (
                                    <MDTypography variant="caption" color="text" display="block" p={1}>Nenhum débito desta ficha serve para este tipo.</MDTypography>
                                )}
                                {debitosDoTipo.map(d => (
                                    <MDBox key={d.id} display="flex" alignItems="center" px={0.5} onClick={() => alternar(d.id)}
                                        sx={{ cursor: "pointer", borderBottom: `1px solid ${cores.divisoria}`, "&:hover": { backgroundColor: cores.hover } }}>
                                        <Checkbox size="small" checked={marcados.includes(d.id)} />
                                        <MDTypography variant="caption" color="text" sx={{ flex: 1 }}>
                                            <strong>{d.titulo}</strong>{d.parcela ? ` (${d.parcela})` : ""} · venc. {dataBr(d.vencimento)} · valor {moeda(d.valor)} ·
                                            saldo {moeda(d.saldo)} · {SITUACAO_DEBITO[d.situacao] || "Aberto"}
                                        </MDTypography>
                                    </MDBox>
                                ))}
                            </MDBox>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <InputLabel sx={rotulo}>Descrição *</InputLabel>
                        <MDInput fullWidth multiline rows={4} value={descricao} onChange={e => setDescricao(e.target.value)}
                            placeholder="O que o suporte precisa fazer e o que precisa saber para isso" inputProps={{ maxLength: 2000 }} />
                    </Grid>

                    <Grid item xs={12}>
                        <InputLabel sx={rotulo}>Anexos {tipo?.exigeAnexo ? "*" : "(opcional)"}</InputLabel>
                        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <MDButton variant="outlined" color="info" size="small" component="label">
                                <Icon sx={{ mr: 0.5 }}>attach_file</Icon>Escolher arquivos
                                <input type="file" hidden multiple onChange={e => {
                                    const novos = Array.from(e.target.files || []);
                                    setArquivos(prev => [...prev, ...novos]);
                                    e.target.value = "";
                                }} />
                            </MDButton>
                            {arquivos.map((a, i) => (
                                <Chip key={`${a.name}-${i}`} size="small" label={a.name} onDelete={() => setArquivos(prev => prev.filter((_, j) => j !== i))} />
                            ))}
                        </MDBox>
                        <MDTypography variant="caption" color="text" display="block" px={1} mt={0.5}>
                            Os anexos vão para a aba Documentos da ficha, ligados à ocorrência do pedido.
                        </MDTypography>
                    </Grid>

                    {erro && (
                        <Grid item xs={12}>
                            <MDTypography variant="caption" color="error" display="block" px={1}>{erro}</MDTypography>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <MDButton variant="text" color="secondary" onClick={onClose} disabled={enviando}>Cancelar</MDButton>
                <MDButton variant="gradient" color="info" onClick={enviar} disabled={enviando}>
                    {enviando ? <CircularProgress size={18} color="inherit" /> : "Enviar ao suporte"}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
}

NovaSolicitacaoDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    idFichaInicial: PropTypes.string,
    onCriada: PropTypes.func,
};

NovaSolicitacaoDialog.defaultProps = {
    idFichaInicial: null,
    onCriada: null,
};
